/**
 * Canonical multipass App.slug values. Use these everywhere a
 * compile-time-checked app slug is needed (cross-service audience,
 * `appSlug` for `createAuthMiddleware`, log labels, etc.).
 *
 * Mirrors the multipass App.slug registry. Workspace directory names
 * differ from slugs in several cases (see ORDO_APP_DIR_TO_SLUG).
 */
// generator:slug-registry-start OrdoAppSlugs
export const OrdoAppSlugs = {
  HQ: 'hq',
  TASKS: 'tasks',
  AGENT: 'agent',
  SCHEDULE: 'schedule',
  COMMS: 'comms',
  AUTH: 'auth',
  RFPS: 'rfps',
  FACTORY: 'factory',
  KIOSK: 'kiosk',
  SCHOOLS: 'schools',
  CHEFS: 'chefs',
  FAMILIES: 'families',
  PREVIEWS: 'previews',
  AGENT_POOL: 'agent-pool',
  ORDO: 'ordo',
  SUBSCRIPTION_RUNNER: 'subscription-runner',
  CUSTOMERS: 'customers',
  MENUS: 'menus',
  // The multipass App.slug is `pdfs` (App 2a0d8fff-a672-4f87-9dd6-3182b2f9e5fd), not the
  // directory name — every role lookup keys on this value.
  PRINTING_PRESS: 'pdfs',
  MCP: 'mcp',
  REPORTING: 'reporting',
  LEADS: 'leads',
  NOTIFICATIONS: 'notifications',
  // generator:slug-registry-end OrdoAppSlugs
} as const

export type OrdoAppSlug = (typeof OrdoAppSlugs)[keyof typeof OrdoAppSlugs]

export const ORDO_APP_SLUGS: readonly OrdoAppSlug[] = Object.freeze(Object.values(OrdoAppSlugs))

/**
 * Workspace directory name → multipass slug. Generator updates this
 * automatically when adding a new app; do NOT hand-edit. Apps without a
 * user-facing multipass slug (e.g. internal tooling) are NOT listed here
 * and live on `ORDO_APP_DIRS_WITHOUT_SLUG` instead.
 */
// generator:slug-registry-start ORDO_APP_DIR_TO_SLUG
export const ORDO_APP_DIR_TO_SLUG: Readonly<Record<string, OrdoAppSlug>> = Object.freeze({
  hq: 'hq',
  tasker: 'tasks',
  agent: 'agent',
  'road-warrior': 'schedule',
  boombox: 'comms',
  multipass: 'auth',
  'rfp-gremlin': 'rfps',
  factory: 'factory',
  previews: 'previews',
  'agent-pool': 'agent-pool',
  ordo: 'ordo',
  'subscription-runner': 'subscription-runner',
  customers: 'customers',
  menus: 'menus',
  'printing-press': 'pdfs',
  mcp: 'mcp',
  reporting: 'reporting',
  leads: 'leads',
  notifications: 'notifications',
  // generator:slug-registry-end ORDO_APP_DIR_TO_SLUG
})

/**
 * Workspace directories that are NOT user-facing multipass apps and
 * therefore intentionally have no slug entry. The CI guard
 * (`bun run constants:check`) treats these as expected absences.
 */
// generator:slug-registry-start ORDO_APP_DIRS_WITHOUT_SLUG
export const ORDO_APP_DIRS_WITHOUT_SLUG: readonly string[] = Object.freeze([
  'factory-scanner',
  'brochure',
  // Shared Helm library chart, not an app.
  '_lib',
  // The 9Router AI gateway — its own dashboard auth, no multipass roles.
  'nine-router',
  // generator:slug-registry-end ORDO_APP_DIRS_WITHOUT_SLUG
])

/**
 * Multipass slug → user-facing brand label. THE single source of truth
 * for app names rendered in UI chrome (page headers, group labels,
 * filter chips, attribution badges). Never use the workspace directory
 * name (`tasker`, `boombox`, `rfp-gremlin`, …) in user-facing strings —
 * always resolve through this map or `getAppDisplayName()`.
 *
 * Generator updates this when adding a new app; do NOT hand-edit
 * inside the markers.
 */
// generator:slug-registry-start ORDO_APP_DISPLAY_NAME
export const ORDO_APP_DISPLAY_NAME: Readonly<Record<OrdoAppSlug, string>> = Object.freeze({
  hq: 'HQ',
  tasks: 'Tasks',
  agent: 'Agent',
  schedule: 'Schedule',
  comms: 'Comms',
  auth: 'Multipass',
  rfps: 'RFPs',
  factory: 'Factory',
  kiosk: 'Kiosk',
  schools: 'Schools',
  chefs: 'Chefs',
  families: 'Families',
  previews: 'Previews',
  'agent-pool': 'Agent Pool',
  ordo: 'Ordo',
  'subscription-runner': 'Subscription Runner',
  customers: 'Customers',
  menus: 'Menus',
  pdfs: 'PDFs',
  mcp: 'MCP',
  reporting: 'Reporting',
  leads: 'Leads',
  notifications: 'Notifications',
  // generator:slug-registry-end ORDO_APP_DISPLAY_NAME
})

/**
 * Resolve a workspace directory name OR a multipass slug OR a tool's
 * `hostApp` identifier into the user-facing brand label.
 *
 * Order of resolution:
 *   1. Use an explicit display name for tools without a Multipass slug.
 *   2. If the input is an alias or workspace dir (`tasker`), map to slug (`tasks`),
 *      then look up the display name (`Tasks`).
 *   3. If the input is already a slug (`tasks`), look up directly.
 *   4. Otherwise (third-party MCP app, dev placeholder, etc.) fall back
 *      to a title-cased rendering of the input so the UI never shows
 *      an empty label.
 *
 * Use this everywhere a group label, filter chip, or attribution badge
 * needs to render an app's brand. Replaces every per-module
 * `APP_LABEL` map that has historically drifted.
 */
/**
 * Aliases for short identifiers used in cross-app payloads (the agent's
 * tool manifests use `rfp` / `road_warrior` rather than the canonical
 * workspace dirs `rfp-gremlin` / `road-warrior`). Maps the alias to a
 * known slug so the display-name lookup hits the curated label
 * instead of falling through to titlecase. Hand-edit safe — outside
 * the generator markers above.
 */
const APP_ID_ALIASES: Readonly<Record<string, OrdoAppSlug>> = Object.freeze({
  rfp: 'rfps',
  road_warrior: 'schedule',
})

// Display-only tools have no Multipass slug or role registry entry.
const APP_NAMES_WITHOUT_SLUG: Readonly<Record<string, string>> = Object.freeze({
  brochure: 'Website',
  'factory-scanner': 'Factory Scanner',
  'nine-router': '9Router',
})

export function getAppDisplayName(idOrSlugOrDir: string): string {
  const toolName = APP_NAMES_WITHOUT_SLUG[idOrSlugOrDir]
  if (typeof toolName === 'string') {
    return toolName
  }
  const aliasSlug = APP_ID_ALIASES[idOrSlugOrDir]
  if (typeof aliasSlug === 'string') {
    return ORDO_APP_DISPLAY_NAME[aliasSlug]
  }
  const dirSlug = (ORDO_APP_DIR_TO_SLUG as Record<string, OrdoAppSlug>)[idOrSlugOrDir]
  if (typeof dirSlug === 'string') {
    return ORDO_APP_DISPLAY_NAME[dirSlug]
  }
  const direct = (ORDO_APP_DISPLAY_NAME as Record<string, string>)[idOrSlugOrDir]
  if (typeof direct === 'string') {
    return direct
  }
  return idOrSlugOrDir
    .split(/[-_]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
}
